import SettingsIcon from '@mui/icons-material/Settings'
import {
	Box,
	Button,
	Paper,
	Switch,
	Table,
	TableBody,
	TableContainer,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import _, { range } from 'lodash'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from '../../components/Modal'
import { getEventHeating24 } from '../../store/EventById/selectors'
import {
	updateOrCreateEventHeating,
	updateOrCreateEventHeatingBulk,
} from '../../store/HouseContainer/operations'
import { getActiveSubControl } from '../../store/SubControlById/selectors'
import { Control, EventHeating, House, Scenario } from '../../types'
import EditHeatingEventForm from './components/EditHeatingEventForm'
import HourlyOverviewBand from './components/HourlyOverviewBand'
import HourlyRuleRow from './components/HourlyRuleRow'

type Props = {
	house: House
	scenario: Scenario
	control: Control
}

// ヒーターは tmpAbove「以下で動作」（EditHeatingEventForm 参照）
const condLabel = (ev: EventHeating): string => `気温 ${+ev.tmpAbove}℃以下`

function HeatingManageTab({ scenario, control }: Props) {
	const activeSubc = useSelector((s) => getActiveSubControl(s, control))
	const eventById = useSelector((s) =>
		getEventHeating24(s, scenario.eventIds, activeSubc),
	)

	const [mode, setMode] = React.useState<'each' | 'bulk'>('each')
	const activeByHour = range(24).map((h) => eventById[h].enabled)

	return (
		<Box sx={{ mt: 1 }}>
			<HourlyOverviewBand activeByHour={activeByHour} />

			<Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
				<ToggleButtonGroup
					size="small"
					exclusive
					value={mode}
					onChange={(_e, v) => v && setMode(v)}
				>
					<ToggleButton value="each">個別編集</ToggleButton>
					<ToggleButton value="bulk">まとめて編集</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{mode === 'bulk' ? (
				<HeatingInlineEditor
					scenarioId={scenario.id}
					subControlId={activeSubc.id}
					eventById={eventById}
				/>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableBody>
							{range(24).map((h) => (
								<EventHeatingTableRow
									key={h}
									h={h}
									ev={eventById[h]}
									scenarioId={scenario.id}
									subControlId={activeSubc.id}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	)
}

// モーダル無しで 24時間を直接インライン編集し、変更分をまとめて保存する。
type InlineProps = {
	scenarioId: number
	subControlId: number
	eventById: Record<number, EventHeating>
}
const HeatingInlineEditor = (props: InlineProps) => {
	const dispatch = useDispatch()
	const { eventById, scenarioId, subControlId } = props
	const [rows, setRows] = React.useState<EventHeating[]>(() =>
		range(24).map((h) => ({ ...eventById[h] })),
	)

	const update = (hour: number, patch: Partial<EventHeating>) =>
		setRows((rs) =>
			rs.map((r) => {
				const [sh] = r.startTime.split(':').map(Number)
				return sh === hour ? { ...r, ...patch } : r
			}),
		)

	const isChanged = (r: EventHeating): boolean => {
		const [sh] = r.startTime.split(':').map(Number)
		const o = eventById[sh]
		return r.enabled !== o.enabled || r.tmpAbove !== o.tmpAbove
	}
	const changed = rows.filter(isChanged)

	const applyFirstToAll = () => {
		const tpl = rows[0]
		setRows((rs) =>
			rs.map((r) => ({ ...r, enabled: tpl.enabled, tmpAbove: tpl.tmpAbove })),
		)
	}

	const save = () => {
		if (!changed.length) return
		dispatch(
			updateOrCreateEventHeatingBulk(
				changed.map((r) => ({
					params: _.omit({ ...r, scenarioId, subControlId }, ['id']),
					eventId: r.id,
				})),
			),
		)
	}

	return (
		<Paper sx={{ p: 1.5 }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 1,
					gap: 1,
					flexWrap: 'wrap',
				}}
			>
				<Typography variant="caption" color="text.secondary">
					各時間帯を直接編集（有効な時間帯に気温しきい値を設定）
				</Typography>
				<Button size="small" onClick={applyFirstToAll}>
					0時を全時間に適用
				</Button>
			</Box>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '3.2rem 4rem 1fr',
					gap: '4px 8px',
					alignItems: 'center',
					maxHeight: 380,
					overflowY: 'auto',
					pr: 0.5,
				}}
			>
				<Typography variant="caption" color="text.secondary">
					時間
				</Typography>
				<Typography variant="caption" color="text.secondary">
					有効
				</Typography>
				<Typography variant="caption" color="text.secondary">
					気温 ℃ 以下で動作
				</Typography>

				{rows.map((r) => {
					const [sh] = r.startTime.split(':').map(Number)
					return (
						<React.Fragment key={sh}>
							<Typography
								variant="body2"
								sx={{ color: r.enabled ? 'text.primary' : 'text.disabled' }}
							>
								{sh}時
							</Typography>
							<Switch
								size="small"
								checked={r.enabled}
								onChange={(e) => update(sh, { enabled: e.target.checked })}
							/>
							<TextField
								type="number"
								size="small"
								value={r.tmpAbove}
								disabled={!r.enabled}
								onChange={(e) =>
									update(sh, { tmpAbove: Number(e.target.value) })
								}
								inputProps={{ step: 0.1 }}
								fullWidth
							/>
						</React.Fragment>
					)
				})}
			</Box>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					gap: 1.5,
					mt: 1.5,
				}}
			>
				<Typography variant="caption" color="text.secondary">
					{changed.length > 0 ? `${changed.length}件 変更あり` : '変更なし'}
				</Typography>
				<Button
					variant="contained"
					disableElevation
					disabled={changed.length === 0}
					onClick={save}
				>
					保存
				</Button>
			</Box>
		</Paper>
	)
}

type RowProps = {
	h: number
	ev: EventHeating
	scenarioId: number
	subControlId: number
}
const EventHeatingTableRow = (props: RowProps) => {
	const [open, setOpen] = React.useState(false)
	const dispatch = useDispatch()
	const { h, ev, scenarioId, subControlId } = props

	return (
		<HourlyRuleRow
			hourLabel={`${h}時`}
			enabled={ev.enabled}
			active={ev.enabled}
			summary={ev.enabled ? condLabel(ev) : 'OFF'}
		>
			<Button variant="outlined" size="small" onClick={() => setOpen(true)}>
				<SettingsIcon fontSize="small" />
			</Button>
			<Modal open={open} closeModal={() => setOpen(false)}>
				<EditHeatingEventForm
					initialValues={_.omit(
						{ ...ev, scenarioId: scenarioId, subControlId },
						['id'],
					)}
					disabled={false}
					handleSubmit={(field) => {
						dispatch(updateOrCreateEventHeating(field, ev.id))
						setOpen(false)
					}}
				/>
			</Modal>
		</HourlyRuleRow>
	)
}

export default HeatingManageTab
